import React, { useCallback, useEffect, useState } from 'react';
import { DatabaseZap, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { hsError } from '../../utils/homeSmartCollaborators';
import { notifyDataChanged } from '../../utils/homeSmartCancellations';
import { ConfirmAction } from './ConfirmAction';

const sampleApi = {
  status: () => api.get('/home-smart/sample-data'),
  load: () => api.post('/home-smart/sample-data', {}, { skipRetry: true, timeout: 90000 }),
  remove: () => api.delete('/home-smart/sample-data', { skipRetry: true, timeout: 90000 }),
};

// Visible only to the account owner (the status call is refused for anyone else).
export const SampleDataCard = ({ onChanged }) => {
  const [status, setStatus] = useState(null);
  const refresh = useCallback(async () => {
    try { setStatus((await sampleApi.status()).data); } catch { setStatus(null); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  if (!status) return null;

  const act = (call, message) => async () => {
    try {
      await call();
      toast.success(message);
      notifyDataChanged();
      await refresh();
      onChanged?.();
    } catch (error) {
      toast.error(hsError(error));
      return false;
    }
  };
  const summary = `${status.properties} immobili · ${status.bookings} prenotazioni · ${status.closures} chiusure · ${status.collaborators} collaboratori`;
  return (
    <section className="flex flex-col gap-4 border border-dashed border-amber-300 bg-amber-50/70 p-4 md:flex-row md:items-center md:justify-between" data-testid="sample-data-card">
      <div className="flex min-w-0 items-start gap-3">
        <DatabaseZap className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
        <div className="min-w-0">
          <p className="font-semibold text-amber-950" data-testid="sample-data-title">{status.present ? 'Stai vedendo dati di esempio' : 'Dati di esempio'}</p>
          <p className="text-sm text-amber-900/80" data-testid="sample-data-summary">
            {status.present ? `${summary}. Non inviano email né comunicazioni a nessuno.` : 'Carica immobili, prenotazioni e chiusure fittizie per provare dashboard e report. Potrai cancellarli quando vuoi.'}
          </p>
        </div>
      </div>
      {status.present ? (
        <ConfirmAction testId="sample-data-remove" label="Cancella dati di esempio" icon={Trash2} destructive className="shrink-0 border-red-200 text-red-700 hover:bg-red-50"
          title="Cancellare i dati di esempio?" description={`Verranno eliminati definitivamente ${summary}. I tuoi dati reali non vengono toccati.`}
          confirmLabel="Cancella" onConfirm={act(sampleApi.remove, 'Dati di esempio cancellati')} />
      ) : (
        <ConfirmAction testId="sample-data-load" label="Carica dati di esempio" icon={Sparkles} className="shrink-0"
          title="Caricare i dati di esempio?" description="Aggiungo 4 immobili, circa 30 prenotazioni passate e future, alcune chiusure e 3 collaboratori fittizi. Nessuno riceverà email."
          confirmLabel="Carica" onConfirm={act(sampleApi.load, 'Dati di esempio caricati')} />
      )}
    </section>
  );
};
